import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { stats } from '../mockData';

const Hero = () => {
  const canvasRef = useRef(null);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 200;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    let animationFrame;
    let bigBangComplete = false;

    class Particle {
      constructor(delay = 0) {
        this.delay = delay;
        this.started = false;
        this.x = centerX;
        this.y = centerY;
        
        // Random angle for explosion
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 3 + 1;
        
        this.speedX = Math.cos(angle) * speed;
        this.speedY = Math.sin(angle) * speed;
        
        this.size = Math.random() * 2.5 + 0.5;
        this.opacity = 0;
        this.maxOpacity = Math.random() * 0.8 + 0.3;
        this.life = 0;
        this.twinkle = Math.random() * 0.02 + 0.01;
        this.twinklePhase = Math.random() * Math.PI * 2;
      }

      update(time) {
        if (time < this.delay) return;
        
        if (!this.started) {
          this.started = true;
        }

        this.life += 0.01;

        // Big bang explosion phase
        if (this.life < 1) {
          this.x += this.speedX * 8; // Fast explosion
          this.y += this.speedY * 8;
          this.opacity = Math.min(this.maxOpacity, this.opacity + 0.05);
        } else {
          // Slow drift phase (like stars)
          this.x += this.speedX * 0.1;
          this.y += this.speedY * 0.1;
          
          // Twinkling effect
          this.twinklePhase += this.twinkle;
          this.opacity = this.maxOpacity + Math.sin(this.twinklePhase) * 0.3;
        }

        // Wrap around edges
        if (this.x > canvas.width + 50) this.x = -50;
        if (this.x < -50) this.x = canvas.width + 50;
        if (this.y > canvas.height + 50) this.y = -50;
        if (this.y < -50) this.y = canvas.height + 50;
      }

      draw() {
        if (!this.started) return;

        // Color variation - blue/cyan/white for star effect
        const colors = [
          `rgba(147, 197, 253, ${this.opacity})`, // Light blue
          `rgba(191, 219, 254, ${this.opacity})`, // Lighter blue
          `rgba(255, 255, 255, ${this.opacity})`, // White
          `rgba(96, 165, 250, ${this.opacity})`   // Medium blue
        ];
        
        const colorIndex = Math.floor(this.size * 1.5) % colors.length;
        ctx.fillStyle = colors[colorIndex];
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();

        // Add glow for larger particles
        if (this.size > 1.5) {
          ctx.shadowBlur = 10;
          ctx.shadowColor = 'rgba(147, 197, 253, 0.5)';
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size * 0.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      }
    }

    // Create particles with staggered delays for cascading effect
    for (let i = 0; i < particleCount; i++) {
      const delay = (i / particleCount) * 50; // Stagger over first 50 frames
      particles.push(new Particle(delay));
    }

    let frameCount = 0;

    function animate() {
      frameCount++;
      
      // Fade effect instead of clear for trail effect during explosion
      if (frameCount < 100) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      } else {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.02)';
        if (!bigBangComplete) {
          bigBangComplete = true;
          setShowStats(true); // Show stats after big bang
        }
      }
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.update(frameCount);
        particle.draw();
      });

      animationFrame = requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="relative min-h-screen bg-black overflow-hidden flex items-center justify-center">
      {/* Particle Canvas Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0"
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 via-black/50 to-black z-0" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-center space-y-8">
          {/* Main Heading with Animation */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-tight">
            <span className="inline-block opacity-0 animate-[fadeIn_0.8s_ease-in_0.3s_forwards]">
              Trasformiamo i tuoi
            </span>
            <br />
            <span className="inline-block opacity-0 animate-[fadeIn_0.8s_ease-in_0.6s_forwards]">
              Profili Social in{' '}
            </span>
            <span className="inline-block bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent opacity-0 animate-[fadeIn_0.8s_ease-in_0.9s_forwards] ml-2">
              Risultati Reali
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-gray-400 text-lg md:text-xl max-w-4xl mx-auto leading-relaxed opacity-0 animate-[fadeIn_0.8s_ease-in_1.2s_forwards]">
            Gestiamo i tuoi Account Social con strategie data-driven che generano Lead Qualificati, 
            Aumentano le Conversioni e valorizzano il tuo Brand.
          </p>

          {/* CTA Button */}
          <div className="pt-6 opacity-0 animate-[fadeIn_0.8s_ease-in_1.5s_forwards]">
            <Button
              onClick={() => scrollToSection('contatti')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-10 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/50"
            >
              Provaci, è gratis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Stats - Show after Big Bang animation */}
          <div className={`pt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 transition-all duration-1000 ${
            showStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center space-y-2 transform transition-all duration-700"
                style={{ 
                  transitionDelay: `${index * 200}ms`,
                  animation: showStats ? `statPulse 0.6s ease-out ${index * 0.2}s` : 'none'
                }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white">
                  {stat.value}
                </div>
                <div className="text-sm md:text-base text-gray-400">
                  {stat.label}
                  <br />
                  <span className="text-xs text-gray-500">{stat.sublabel}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
